import { localDateString } from '../../../../utils/helpers/localDate';
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import {
    matureInvestment,
    preMatureInvestment,
    renewInvestment,
    calculateInvestmentPreview,
    clearInvestmentPreview,
} from '../../../../redux/actions/SocietyPortal/InvestmentAction/InvestmentAction';
import { getGLAccounts } from '../../../../redux/actions/SocietyPortal/GLAccountAction/GLAccountAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';

const INTEREST_MODES = [
    { value: 'simple', label: 'Simple' },
    { value: 'compound', label: 'Compound' },
];

const FREQUENCIES = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'half_yearly', label: 'Half-Yearly' },
    { value: 'annual', label: 'Annual' },
    { value: 'bullet', label: 'Bullet' },
    { value: 'quarter_end', label: 'Quarter End' },
    { value: 'month_end', label: 'Month End' },
];

const inputClass = 'tw-w-full tw-px-2.5 tw-py-1.5 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500';
const labelClass = 'tw-block tw-text-[11px] tw-font-medium tw-text-gray-700 tw-mb-1';
const req = <span className="tw-text-red-500">*</span>;

const todayISO = () => localDateString();

const formatCurrency = (amount) =>
    parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ACTION_CONFIG = {
    mature: {
        title: 'Mature Investment',
        description: 'Mark this investment as matured. This will post the maturity journal entry.',
        icon: 'hugeicons:checkmark-circle-02',
        accent: 'tw-from-blue-500 tw-to-indigo-600',
        button: 'tw-bg-blue-600 hover:tw-bg-blue-700',
        buttonLabel: 'Mature Investment',
    },
    pre_mature: {
        title: 'Pre-Mature Investment',
        description: 'Close this investment before its maturity date. Optionally add a penalty amount.',
        icon: 'hugeicons:alert-circle',
        accent: 'tw-from-amber-500 tw-to-orange-600',
        button: 'tw-bg-amber-600 hover:tw-bg-amber-700',
        buttonLabel: 'Confirm Pre-Maturity',
    },
    renew: {
        title: 'Renew Investment',
        description: 'Create a new investment linked to this one. The current investment will be marked as renewed.',
        icon: 'hugeicons:refresh',
        accent: 'tw-from-purple-500 tw-to-fuchsia-600',
        button: 'tw-bg-purple-600 hover:tw-bg-purple-700',
        buttonLabel: 'Renew Investment',
    },
};

const InvestmentActionModal = ({ action, investment, onClose, onSuccess }) => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const { glAccountsData, loading: glAccountsLoading } = useSelector(state => state.GLAccountReducer || {});
    const { matureLoading, preMatureLoading, renewLoading, preview, previewLoading } = useSelector(state => state.InvestmentReducer || {});

    const config = ACTION_CONFIG[action] || ACTION_CONFIG.mature;
    const isMature = action === 'mature';
    const isPreMature = action === 'pre_mature';
    const isRenew = action === 'renew';

    const saving =
        (isMature && matureLoading) ||
        (isPreMature && preMatureLoading) ||
        (isRenew && renewLoading);

    // Mature state
    const [matureForm, setMatureForm] = useState({
        maturity_date: investment?.maturity_date?.split('T')[0] || todayISO(),
        maturity_amount: investment?.maturity_amount ? String(parseFloat(investment.maturity_amount)) : '',
        bank_account_cd: investment?.credit_account_cd || '',
        remarks: '',
    });

    // Pre-Mature state
    const [preMatureForm, setPreMatureForm] = useState({
        pre_mature_date: todayISO(),
        settlement_amount: '',
        penalty_amount: '',
        bank_account_cd: investment?.credit_account_cd || '',
        remarks: '',
    });

    // Renew state
    const [renewForm, setRenewForm] = useState({
        investment_date: todayISO(),
        investment_number: '',
        principal_amount: investment?.maturity_amount ? String(parseFloat(investment.maturity_amount)) : '',
        interest_rate: investment?.interest_rate ? String(parseFloat(investment.interest_rate)) : '',
        interest_mode: investment?.interest_mode || 'simple',
        frequency: investment?.frequency || 'quarterly',
        maturity_date: '',
        remarks: `Renewed from ${investment?.investment_number || ''}`,
    });

    useEffect(() => {
        if (societyId && (!glAccountsData || glAccountsData.length === 0)) {
            dispatch(getGLAccounts({ societyId }));
        }
    }, [dispatch, societyId, glAccountsData]);

    // Debounced auto-preview for renew action
    useEffect(() => {
        if (!isRenew) return;

        const { investment_date, maturity_date, principal_amount, interest_rate, interest_mode, frequency } = renewForm;
        const principal = parseFloat(principal_amount);
        const rate = parseFloat(interest_rate);

        if (!investment_date || !maturity_date || !interest_mode || !frequency) {
            dispatch(clearInvestmentPreview());
            return;
        }
        if (!principal || principal <= 0 || isNaN(rate) || rate < 0) {
            dispatch(clearInvestmentPreview());
            return;
        }
        if (new Date(maturity_date) <= new Date(investment_date)) {
            dispatch(clearInvestmentPreview());
            return;
        }

        const timer = setTimeout(() => {
            dispatch(calculateInvestmentPreview({
                investment_date,
                maturity_date,
                principal_amount: principal,
                interest_rate: rate,
                interest_mode,
                frequency,
            }));
        }, 400);

        return () => clearTimeout(timer);
    }, [
        dispatch,
        isRenew,
        renewForm.investment_date,
        renewForm.maturity_date,
        renewForm.principal_amount,
        renewForm.interest_rate,
        renewForm.interest_mode,
        renewForm.frequency,
    ]);

    // Clear preview on unmount
    useEffect(() => () => { dispatch(clearInvestmentPreview()); }, [dispatch]);

    const accountOptions = useMemo(() => {
        if (!glAccountsData) return [];
        return (glAccountsData || []).map(acc => ({
            value: acc.ac_code,
            label: `${acc.ac_code} - ${acc.name}`,
        }));
    }, [glAccountsData]);

    const handleSubmit = (e) => {
        e.preventDefault();
        const baseData = {
            societyId,
            id: investment.id,
            onSuccess: () => onSuccess?.(),
        };

        if (isMature) {
            if (!matureForm.maturity_date || !matureForm.maturity_amount) {
                toast.warn('Maturity date and amount are required');
                return;
            }
            dispatch(matureInvestment({
                ...baseData,
                maturity_date: matureForm.maturity_date,
                maturity_amount: parseFloat(matureForm.maturity_amount),
                bank_account_cd: matureForm.bank_account_cd || undefined,
                remarks: matureForm.remarks || undefined,
            }));
        } else if (isPreMature) {
            if (!preMatureForm.pre_mature_date || !preMatureForm.settlement_amount) {
                toast.warn('Pre-mature date and settlement amount are required');
                return;
            }
            dispatch(preMatureInvestment({
                ...baseData,
                pre_mature_date: preMatureForm.pre_mature_date,
                settlement_amount: parseFloat(preMatureForm.settlement_amount),
                penalty_amount: preMatureForm.penalty_amount ? parseFloat(preMatureForm.penalty_amount) : 0,
                bank_account_cd: preMatureForm.bank_account_cd || undefined,
                remarks: preMatureForm.remarks || undefined,
            }));
        } else if (isRenew) {
            const required = ['investment_date', 'investment_number', 'principal_amount', 'interest_rate', 'maturity_date'];
            for (const f of required) {
                if (!renewForm[f]) {
                    toast.warn(`${f.replace(/_/g, ' ')} is required`);
                    return;
                }
            }
            dispatch(renewInvestment({
                ...baseData,
                investment_date: renewForm.investment_date,
                investment_number: renewForm.investment_number.trim(),
                principal_amount: parseFloat(renewForm.principal_amount),
                interest_rate: parseFloat(renewForm.interest_rate),
                interest_mode: renewForm.interest_mode,
                frequency: renewForm.frequency,
                maturity_date: renewForm.maturity_date,
                remarks: renewForm.remarks || undefined,
            }));
        }
    };

    return (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/40" onClick={onClose}>
            <div
                className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-2xl tw-mx-4 tw-max-h-[92vh] tw-overflow-hidden tw-flex tw-flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-b tw-border-gray-100">
                    <div className="tw-flex tw-items-center tw-gap-2.5">
                        <div className={`tw-w-9 tw-h-9 tw-rounded-lg tw-bg-gradient-to-br ${config.accent} tw-flex tw-items-center tw-justify-center`}>
                            <Icon icon={config.icon} className="tw-text-white tw-text-base" />
                        </div>
                        <div>
                            <h2 className="tw-text-sm tw-font-bold tw-text-gray-800">{config.title}</h2>
                            <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">{investment?.investment_number}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500">
                        <Icon icon="mdi:close" className="tw-text-lg" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="tw-flex-1 tw-overflow-y-auto">
                    <div className="tw-p-5 tw-space-y-4">
                        <div className="tw-text-xs tw-text-gray-600 tw-bg-gray-50 tw-border tw-border-gray-100 tw-rounded-md tw-p-3">
                            {config.description}
                        </div>

                        {/* MATURE */}
                        {isMature && (
                            <>
                                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
                                    <div>
                                        <label className={labelClass}>Maturity Date {req}</label>
                                        <input type="date" value={matureForm.maturity_date} onChange={(e) => setMatureForm({ ...matureForm, maturity_date: e.target.value })} className={inputClass} required />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Maturity Amount (₹) {req}</label>
                                        <input type="number" min="0" step="0.01" value={matureForm.maturity_amount} onChange={(e) => setMatureForm({ ...matureForm, maturity_amount: e.target.value })} className={inputClass} required />
                                    </div>
                                </div>
                                <div>
                                    <label className={labelClass}>Bank Account (Dr)</label>
                                    <SearchableDropdown
                                        value={matureForm.bank_account_cd || 'all'}
                                        onChange={(v) => setMatureForm({ ...matureForm, bank_account_cd: v === 'all' ? '' : v })}
                                        options={accountOptions}
                                        placeholder="Select bank account"
                                        loading={glAccountsLoading}
                                        small
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Remarks</label>
                                    <textarea rows={2} value={matureForm.remarks} onChange={(e) => setMatureForm({ ...matureForm, remarks: e.target.value })} placeholder="Optional" className={`${inputClass} tw-resize-none`} />
                                </div>
                            </>
                        )}

                        {/* PRE-MATURE */}
                        {isPreMature && (
                            <>
                                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
                                    <div>
                                        <label className={labelClass}>Pre-Mature Date {req}</label>
                                        <input type="date" value={preMatureForm.pre_mature_date} onChange={(e) => setPreMatureForm({ ...preMatureForm, pre_mature_date: e.target.value })} className={inputClass} required />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Settlement Amount (₹) {req}</label>
                                        <input type="number" min="0" step="0.01" value={preMatureForm.settlement_amount} onChange={(e) => setPreMatureForm({ ...preMatureForm, settlement_amount: e.target.value })} placeholder="Amount received after penalty" className={inputClass} required />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Penalty Amount (₹)</label>
                                        <input type="number" min="0" step="0.01" value={preMatureForm.penalty_amount} onChange={(e) => setPreMatureForm({ ...preMatureForm, penalty_amount: e.target.value })} placeholder="0" className={inputClass} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Bank Account (Dr)</label>
                                        <SearchableDropdown
                                            value={preMatureForm.bank_account_cd || 'all'}
                                            onChange={(v) => setPreMatureForm({ ...preMatureForm, bank_account_cd: v === 'all' ? '' : v })}
                                            options={accountOptions}
                                            placeholder="Select bank account"
                                            loading={glAccountsLoading}
                                            small
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className={labelClass}>Remarks</label>
                                    <textarea rows={2} value={preMatureForm.remarks} onChange={(e) => setPreMatureForm({ ...preMatureForm, remarks: e.target.value })} placeholder="Reason for early withdrawal" className={`${inputClass} tw-resize-none`} />
                                </div>
                            </>
                        )}

                        {/* RENEW */}
                        {isRenew && (
                            <>
                                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                                    <div>
                                        <label className={labelClass}>New Investment Date {req}</label>
                                        <input type="date" value={renewForm.investment_date} onChange={(e) => setRenewForm({ ...renewForm, investment_date: e.target.value })} className={inputClass} required />
                                    </div>
                                    <div>
                                        <label className={labelClass}>New FD Number {req}</label>
                                        <input type="text" value={renewForm.investment_number} onChange={(e) => setRenewForm({ ...renewForm, investment_number: e.target.value })} placeholder="FD/2026-27/001" className={inputClass} required />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Principal (₹) {req}</label>
                                        <input type="number" min="0" step="0.01" value={renewForm.principal_amount} onChange={(e) => setRenewForm({ ...renewForm, principal_amount: e.target.value })} className={inputClass} required />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Interest Rate (%) {req}</label>
                                        <input type="number" min="0" step="0.01" value={renewForm.interest_rate} onChange={(e) => setRenewForm({ ...renewForm, interest_rate: e.target.value })} className={inputClass} required />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Interest Mode {req}</label>
                                        <SearchableDropdown
                                            value={renewForm.interest_mode}
                                            onChange={(v) => setRenewForm({ ...renewForm, interest_mode: v })}
                                            options={INTEREST_MODES}
                                            clearable={false}
                                            small
                                        />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Frequency {req}</label>
                                        <SearchableDropdown
                                            value={renewForm.frequency}
                                            onChange={(v) => setRenewForm({ ...renewForm, frequency: v })}
                                            options={FREQUENCIES}
                                            clearable={false}
                                            small
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className={labelClass}>New Maturity Date {req}</label>
                                    <input type="date" value={renewForm.maturity_date} onChange={(e) => setRenewForm({ ...renewForm, maturity_date: e.target.value })} className={inputClass} required />
                                </div>

                                {/* Interest Preview */}
                                {(preview || previewLoading) && (
                                    <div className="tw-border tw-border-emerald-100 tw-rounded-lg tw-overflow-hidden tw-bg-emerald-50/40">
                                        <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-2 tw-bg-emerald-50 tw-border-b tw-border-emerald-100">
                                            {previewLoading ? (
                                                <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-emerald-700">
                                                    <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-emerald-300 tw-border-t-emerald-600"></div>
                                                    Calculating preview...
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="tw-text-xs tw-text-emerald-700">
                                                        Total Interest: <span className="tw-font-bold">₹{formatCurrency(preview?.totalInterest)}</span>
                                                    </div>
                                                    <div className="tw-text-xs tw-text-emerald-700">
                                                        Maturity Amount: <span className="tw-font-bold">₹{formatCurrency(preview?.maturityAmount)}</span>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        {preview?.schedule && preview.schedule.length > 0 && (
                                            <div className="tw-max-h-[200px] tw-overflow-y-auto">
                                                <table className="tw-w-full tw-text-xs">
                                                    <thead className="tw-sticky tw-top-0 tw-bg-white tw-z-10">
                                                        <tr className="tw-border-b tw-border-emerald-100">
                                                            <th className="tw-text-left tw-px-4 tw-py-2 tw-font-semibold tw-text-gray-600 tw-text-[11px]">Date</th>
                                                            <th className="tw-text-right tw-px-4 tw-py-2 tw-font-semibold tw-text-gray-600 tw-text-[11px]">Amount</th>
                                                            <th className="tw-text-right tw-px-4 tw-py-2 tw-font-semibold tw-text-gray-600 tw-text-[11px]">Opening Bal.</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {preview.schedule.map((row, idx) => (
                                                            <tr key={idx} className="tw-border-b tw-border-emerald-50 last:tw-border-0 hover:tw-bg-white/60">
                                                                <td className="tw-px-4 tw-py-1.5 tw-text-gray-700">{row.accrued_date}</td>
                                                                <td className="tw-px-4 tw-py-1.5 tw-text-right tw-text-gray-700">₹{formatCurrency(row.accrued_amount)}</td>
                                                                <td className="tw-px-4 tw-py-1.5 tw-text-right tw-text-gray-700">₹{formatCurrency(row.opening_balance)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div>
                                    <label className={labelClass}>Remarks</label>
                                    <textarea rows={2} value={renewForm.remarks} onChange={(e) => setRenewForm({ ...renewForm, remarks: e.target.value })} className={`${inputClass} tw-resize-none`} />
                                </div>
                            </>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-px-5 tw-py-3 tw-border-t tw-border-gray-100 tw-bg-gray-50">
                        <button
                            type="button"
                            onClick={onClose}
                            className="tw-px-4 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className={`tw-flex tw-items-center tw-gap-1 tw-px-4 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-white ${config.button} disabled:tw-opacity-50 disabled:tw-cursor-not-allowed`}
                        >
                            {saving && <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-white tw-border-t-transparent"></div>}
                            {config.buttonLabel}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default InvestmentActionModal;
